import { ListenerMiddlewareInstance, isAnyOf } from '@reduxjs/toolkit';
import {
  editDescriptor,
  rebaseEditorProject,
  redoLatestUndo,
  revertCurrentDescriptor,
  saveProjectPending,
  saveProjectSuccess,
  undoLatestChange,
} from '../editor/actions';
import {
  selectCurrentDescriptorDiffPatch,
  selectEditorHasUnsavedChanges,
  selectIsProjectEditable,
  selectProjectId,
  selectProjectVersion,
} from '../editor/selectors';
import { showNotification } from '../notifications/actions';
import { receiveMultiUserEvent, sendMultiUserEvent } from './multiuser/actions';
import { setCanvasPreviewSize } from './preview/actions';
import savePreviewSize from './preview/utils/savePreviewSize';
import mergeIncomingAppProjectChanges from './projects/actions/mergeIncomingAppProjectChanges';
import { AppProjectEditorRootState } from './reducer';
import { setTheme } from '../theming/actions';
import saveTheme from '../theming/utils/saveTheme';
import { closeComponentIsolationMode, openComponentIsolationMode, setCurrentView } from './manipulation/actions';
import setLastOpenedViewId from './pages/utils/setLastOpenedViewId';
import storeAppProjectDraft from './drafts/remote/storeAppProjectDraft';
import AsyncDebouncer from '../utilities/AsyncDebouncer';
import commitIsolatedComponentInstanceChangesToDescription from './components/actions/commitIsolatedComponentInstanceChangesToDescription';
import { selectCurrentView, selectFullIsolatedComponentInstance } from './dolan/selectors';

export default function setupAppProjectEditorSideEffects(
  listenerMiddleware: ListenerMiddlewareInstance<AppProjectEditorRootState>,
): void {
  const storeAppProjectDraftDebouncer = new AsyncDebouncer({
    execute: storeAppProjectDraft,
    debounceDelay: 1_000,
    maxWait: 10_000,
  });
  const shouldImmediatelyStoreAppProjectDraft = isAnyOf(
    saveProjectSuccess,
    rebaseEditorProject,
    revertCurrentDescriptor,
  );

  /**
   * Update locally stored changes whenever there are actions that
   * would mutate the difference on locally edited descriptor and the remote desctiptor.
   */
  listenerMiddleware.startListening({
    matcher: isAnyOf(
      editDescriptor,
      saveProjectSuccess,
      rebaseEditorProject,
      revertCurrentDescriptor,
      undoLatestChange,
      redoLatestUndo,
    ),
    effect: async (action, api) => {
      const state = api.getState();
      const projectId = selectProjectId(state);
      const diffPatch = selectCurrentDescriptorDiffPatch(state);
      const version = selectProjectVersion(state);
      const editable = selectIsProjectEditable(state);
      if (editable && projectId && version != null && diffPatch) {
        // Store unsaved changes to the known remote state
        storeAppProjectDraftDebouncer.schedule(projectId, version, diffPatch);
        // For specific actions force store immediately
        if (shouldImmediatelyStoreAppProjectDraft(action)) {
          storeAppProjectDraftDebouncer.flush();
        }
      }
    },
  });

  /**
   * Force attempt to send any unsaved changes when navigating away.
   */
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => storeAppProjectDraftDebouncer.flush());
  }

  /**
   * Notify other users when the active page changes
   */
  listenerMiddleware.startListening({
    actionCreator: setCurrentView,
    effect: (_action, { dispatch, getState }) => {
      const state = getState();
      const newCurrentView = selectCurrentView(state);
      const hasUnsavedChanges = selectEditorHasUnsavedChanges(state);
      if (newCurrentView) {
        dispatch(
          sendMultiUserEvent('open_page', {
            name: newCurrentView.display_name,
            unsaved_changes: hasUnsavedChanges,
          }),
        );
      }
    },
  });

  /**
   * Notify other users when there are local edits done by the user
   */
  listenerMiddleware.startListening({
    predicate: (_action, currentState, previousState) => {
      const hadUnsavedChanges = selectEditorHasUnsavedChanges(previousState);
      const hasUnsavedChanges = selectEditorHasUnsavedChanges(currentState);
      return !hadUnsavedChanges && hasUnsavedChanges;
    },
    effect: (_action, { dispatch }) => {
      dispatch(sendMultiUserEvent('unsaved_changes'));
    },
  });

  /**
   * Notify other users when there are no more local edits done by the user,
   * unless this change is due to a save.
   */
  listenerMiddleware.startListening({
    predicate: (action, currentState, previousState) => {
      const hadUnsavedChanges = selectEditorHasUnsavedChanges(previousState);
      const hasUnsavedChanges = selectEditorHasUnsavedChanges(currentState);
      return hadUnsavedChanges && !hasUnsavedChanges && !saveProjectSuccess.match(action);
    },
    effect: (_action, { dispatch }) => {
      dispatch(sendMultiUserEvent('no_changes'));
    },
  });

  /**
   * React to certain types of multi-user events
   */
  listenerMiddleware.startListening({
    actionCreator: receiveMultiUserEvent,
    effect: (action, { dispatch }) => {
      // Additional logic on certain events
      const { event } = action.payload;
      if (event?.type === 'save') {
        // Somebody else just saved changes to this app
        dispatch(showNotification('info', `${event.user_display_name} made changes: ${event.details}.`));
        // We expect that there are incoming changes
        dispatch(mergeIncomingAppProjectChanges());
      }
    },
  });

  /**
   * Persist any change to the selected canvas preview size.
   */
  listenerMiddleware.startListening({
    actionCreator: setCanvasPreviewSize,
    effect: (action) => {
      savePreviewSize(action.payload);
    },
  });

  /**
   * Persist the last visited view.
   */
  listenerMiddleware.startListening({
    actionCreator: setCurrentView,
    effect: (action, api) => {
      const state = api.getState();
      const appId = selectProjectId(state);
      const viewId = action.payload;
      if (appId != null && viewId != null) {
        setLastOpenedViewId(appId, viewId);
      }
    },
  });

  /**
   * Persist theme change.
   */
  listenerMiddleware.startListening({
    actionCreator: setTheme,
    effect: (action) => {
      saveTheme(action.payload);
    },
  });

  /**
   * When attempting to save, commit any changes to a full isolated component instance.
   */
  listenerMiddleware.startListening({
    actionCreator: saveProjectPending,
    effect: (_action, api) => {
      const state = api.getState();
      const fullIsolatedComponentInstance = selectFullIsolatedComponentInstance(state);
      if (fullIsolatedComponentInstance) {
        api.dispatch(commitIsolatedComponentInstanceChangesToDescription(fullIsolatedComponentInstance));
      }
    },
  });

  /**
   * When exiting isolation mode (or changing the component or current view), commit any changes to
   * a full isolated component instance.
   */
  listenerMiddleware.startListening({
    matcher: isAnyOf(closeComponentIsolationMode, openComponentIsolationMode, setCurrentView),
    effect: (_action, api) => {
      const originalState = api.getOriginalState();
      const fullIsolatedComponentInstance = selectFullIsolatedComponentInstance(originalState);
      if (fullIsolatedComponentInstance) {
        api.dispatch(commitIsolatedComponentInstanceChangesToDescription(fullIsolatedComponentInstance));
      }
    },
  });
}
